<script setup lang="ts">
/**
 * Page d'erreur globale de Nuxt.
 *
 * ─── POURQUOI ELLE NE REDIRIGE PLUS (incident SEO 2026-08-07) ─────────────
 * Elle rendait un `<div />` VIDE puis, au montage côté client, appelait
 * `clearError({ redirect: "/erreur?code=..." })`.
 *
 * Deux conséquences pour Google, constatées dans Search Console sur la PAGE
 * D'ACCUEIL (crawl du 2026-08-06 04:59 UTC) :
 *
 *   googleCanonical   https://donoor.org/
 *   userCanonical     https://donoor.org/erreur     ← la home se déclarait
 *                                                     doublon de la page d'erreur
 *
 * 1. En rendu serveur, la page était VIDE. Googlebot recevait une page sans
 *    contenu à l'URL demandée.
 * 2. Googlebot exécute le JavaScript : il suivait la redirection vers /erreur
 *    et retenait le canonical de CETTE page. N'importe quelle erreur passagère
 *    sur une URL pouvait donc faire enregistrer « cette page est un doublon de
 *    /erreur » — y compris sur la page d'accueil.
 *
 * ─── LA RÈGLE ────────────────────────────────────────────────────────────
 * Une erreur ne doit JAMAIS faire changer d'URL. Elle s'affiche sur place,
 * avec le bon code HTTP et une balise `noindex`. C'est ce que fait ce fichier
 * désormais : même rendu visuel qu'avant, zéro redirection, `noindex` posé dès
 * le rendu serveur — donc même si Googlebot n'exécute pas le JavaScript, il ne
 * peut ni indexer la page ni la prendre pour une autre.
 *
 * La page /erreur reste en place : elle sert aux redirections explicites
 * déclarées ailleurs (ex. `register.vue`). Elle est déjà en `noindex`.
 *
 * ─── POURQUOI LE HEADER ET LE FOOTER SONT RÉPÉTÉS ICI ────────────────────
 * Nuxt rend `error.vue` À LA PLACE de `app.vue` : la mise en page globale
 * n'est pas appliquée. C'était justement le rôle de l'ancienne redirection
 * vers /erreur — retrouver le header et le footer.
 * Comme on ne redirige plus, on les monte explicitement ici. Le visiteur
 * retrouve donc exactement la même page qu'avant (navbar + contenu + footer),
 * mais sans changement d'URL et sans le canonical pollué.
 */
import { navLinks } from "~/utils/navLinks";

const error = useError();
const { t } = useI18n();

// INSTANTANÉ, VOLONTAIREMENT NON RÉACTIF (bug repéré par Karim 2026-08-08).
// Avec des `computed` sur `error.value`, cliquer « Retour à l'accueil »
// appelait `clearError()`, qui met l'erreur à null AVANT que la navigation
// n'aboutisse : `Number(undefined) || 500` repassait alors à 500 et la page
// réaffichait « 500 — Une erreur est survenue » en plein écran pendant la
// transition, y compris par-dessus la page d'accueil en cours de chargement.
// On fige donc les valeurs au montage : elles décrivent l'erreur qui a amené
// ici, elles n'ont aucune raison de changer ensuite.
const statusCode = Number(error.value?.statusCode) || 404;
const is404 = statusCode === 404;
const rawMessage = error.value?.message ?? "";
const message = is404
  ? t("navigation.pageErreur.message404")
  : // Jamais le message technique brut : il contient l'URL de l'API et le
    // verbe HTTP (« [GET] "https://api.donoor.org/…": 404 »), illisible pour
    // un visiteur et inutilement révélateur de l'infrastructure.
    t("navigation.pageErreur.messageErreur");
// Conservé pour le diagnostic navigateur uniquement, jamais affiché.
if (import.meta.client && rawMessage) console.warn("[erreur]", rawMessage);

// ─── FILET « CHUNK DISPARU APRÈS DÉPLOIEMENT » (incident 2026-09-11) ──────
// Un onglet ouvert avant un déploiement garde le manifeste de l'ancien build.
// Le chunk suivant n'existe plus, Vercel renvoie la page SPA en `text/html`,
// le navigateur refuse ce MIME pour un module et l'import échoue :
//   « Failed to fetch dynamically imported module: …/_nuxt/DhrQYzRI.js »
// `experimental.emitRouteChunkError: "automatic-immediate"` (nuxt.config)
// intercepte déjà le cas normal via le hook `app:chunkError`. Mais l'échec
// peut aussi remonter en promesse non capturée, hors de ce circuit : le
// visiteur voyait alors « Une erreur est survenue » pour un incident qu'un
// simple rechargement répare.
//
// On recharge donc la MÊME URL — jamais de changement d'URL, cf. la règle SEO
// en tête de fichier — avec un verrou de 30 s par chemin : si le rechargement
// ne suffit pas, la page d'erreur s'affiche normalement au lieu de boucler.
const ERREUR_CHUNK =
  /dynamically imported module|Importing a module script failed|ChunkLoadError|error loading dynamically imported module/i;

if (import.meta.client && !is404 && ERREUR_CHUNK.test(rawMessage)) {
  const CLE = "donoor:rechargement-chunk";
  const MARQUEUR = "\u0000donoor:rechargement-chunk=";
  const chemin = window.location.pathname + window.location.search;

  // Le verrou a besoin d'un support qui SURVIT au rechargement. `sessionStorage`
  // le fait partout… sauf dans l'aperçu du dashboard : la page publique y est
  // chargée en iframe TIERCE, et le navigateur partitionne (ou refuse) le
  // stockage d'une iframe tierce. L'ancienne version renonçait alors au
  // rechargement — c'est exactement là que le filet devait agir, et l'aperçu
  // restait bloqué sur « Oups, une erreur est survenue » après un déploiement
  // pendant que la même page, ouverte seule, marchait. (Incident 2026-09-11.)
  //
  // Repli : `window.name`, qui survit au rechargement dans le MÊME cadre, n'est
  // pas soumis aux permissions de stockage, et reste privé à ce cadre. On le
  // préfixe d'un marqueur pour ne jamais écraser la valeur qu'un tiers y aurait
  // laissée.
  const lireVerrou = (): string | null => {
    try {
      return window.sessionStorage.getItem(CLE);
    } catch {
      const nom = window.name || "";
      const i = nom.indexOf(MARQUEUR);
      return i === -1 ? null : nom.slice(i + MARQUEUR.length);
    }
  };
  const ecrireVerrou = (valeur: string) => {
    try {
      window.sessionStorage.setItem(CLE, valeur);
    } catch {
      const nom = window.name || "";
      window.name = (nom.split(MARQUEUR)[0] ?? "") + MARQUEUR + valeur;
    }
  };

  let dejaTente = false;
  try {
    const brut = lireVerrou();
    const etat = brut ? (JSON.parse(brut) as { chemin?: string; expire?: number }) : null;
    dejaTente = etat?.chemin === chemin && Number(etat?.expire) > Date.now();
  } catch {
    // Verrou illisible (valeur corrompue) : on le réécrit plutôt que de
    // renoncer — le rechargement suivant retrouvera un verrou valide.
    dejaTente = false;
  }
  if (!dejaTente) {
    ecrireVerrou(JSON.stringify({ chemin, expire: Date.now() + 30_000 }));
    window.location.reload();
  }
}

useSeoMeta({
  title: is404 ? t("navigation.pageErreur.titre404") : t("navigation.pageErreur.titreErreur"),
  // `noindex, nofollow` rendu CÔTÉ SERVEUR : la protection ne dépend pas de
  // l'exécution du JavaScript par le robot.
  robots: "noindex, nofollow",
});

// Le retour à l'accueil est un LIEN NATIF (cf. template : `to="/" external`),
// pas un gestionnaire de clic.
//
// Historique de deux tentatives ratées, pour ne pas les refaire :
//  1. `clearError({ redirect: "/" })` — la forme recommandée par Nuxt —
//     recharge intégralement la page : 1 à 2 secondes d'écran figé.
//  2. `clearError()` puis `navigateTo("/")` — PIRE : on démonte la page
//     d'erreur AVANT que l'accueil soit prêt, si bien qu'on ne voyait plus
//     que le footer sur fond vide pendant le chargement.
//
// Un lien natif règle les deux : le navigateur garde la page courante affichée
// jusqu'à ce que le nouveau document soit prêt, puis bascule d'un coup. Aucun
// état intermédiaire vide — le comportement d'un site classique.
</script>

<template>
  <div>
    <AppHeader :links="navLinks" />

    <div
      class="min-h-screen flex items-center justify-center relative overflow-hidden"
      style="background: linear-gradient(135deg, #f0f7ff 0%, #e8f4fc 25%, #f5f9ff 50%, #fafcff 100%)"
    >
      <!-- Subtle gradient overlay -->
      <div
        class="absolute inset-0 pointer-events-none"
        style="background: radial-gradient(ellipse 80% 60% at 30% 40%, rgba(31,154,254,0.08) 0%, transparent 70%)"
      />

      <!-- Floating decorative elements -->
      <div class="absolute inset-0 overflow-hidden pointer-events-none">
        <div class="absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-[0.08]" style="background: linear-gradient(135deg, var(--color-main) 0%, var(--color-main2) 100%)" />
        <div class="absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-[0.06]" style="background: linear-gradient(135deg, var(--color-main2) 0%, var(--color-main) 100%)" />
        <div class="absolute top-1/4 left-1/4 w-3 h-3 rounded-full bg-main/20 animate-float-slow" />
        <div class="absolute top-1/3 right-1/3 w-2 h-2 rounded-full bg-main2/20 animate-float-slower" />
        <div class="absolute bottom-1/4 right-1/4 w-4 h-4 rounded-full bg-main/15 animate-float" />
      </div>

      <UContainer class="relative z-10">
        <div class="text-center max-w-2xl mx-auto px-4">
          <!-- 404 Error -->
          <div v-if="is404">
            <div class="err-anim err-zoom">
              <div class="text-[120px] sm:text-[160px] lg:text-[200px] font-display leading-none mb-2 bg-gradient-to-r from-main to-main2 bg-clip-text text-transparent">
                404
              </div>
            </div>

            <div class="err-anim err-monte" style="animation-delay: 0.2s">
              <h1 class="text-2xl sm:text-3xl lg:text-4xl font-display text-[#1a1a2e] mb-4">{{ $t("navigation.pageErreur.titre404") }}</h1>
            </div>

            <div class="err-anim err-monte" style="animation-delay: 0.3s">
              <p class="text-base sm:text-lg text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
                {{ $t("navigation.pageErreur.desole404") }}
              </p>
            </div>
          </div>

          <!-- Other Errors -->
          <div v-else>
            <div class="err-anim err-zoom">
              <div class="text-[120px] sm:text-[160px] lg:text-[200px] font-display leading-none mb-2 bg-gradient-to-r from-main to-main2 bg-clip-text text-transparent">
                {{ statusCode }}
              </div>
            </div>

            <div class="err-anim err-monte" style="animation-delay: 0.2s">
              <h1 class="text-2xl sm:text-3xl lg:text-4xl font-display text-[#1a1a2e] mb-4">{{ $t("navigation.pageErreur.oups") }}</h1>
            </div>

            <div class="err-anim err-monte" style="animation-delay: 0.3s">
              <p class="text-base sm:text-lg text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">{{ message }}</p>
            </div>
          </div>

          <!-- Actions -->
          <div class="err-anim err-monte" style="animation-delay: 0.4s">
            <div class="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <UButton
                to="/"
                external
                size="lg"
                color="neutral"
                class="rounded-full font-semibold bg-[#1a1a2e] text-white hover:bg-[#2a2a3e] active:bg-[#1a1a2e] transition-all px-8 py-3 text-[15px] shadow-lg shadow-gray-900/10 whitespace-nowrap"
              >
                <UIcon name="i-lucide-home" class="w-4 h-4 mr-2" />
                {{ $t("commun.actions.retour") }}
              </UButton>
              <UButton
                to="https://wa.me/33743715404"
                target="_blank"
                size="lg"
                color="neutral"
                class="rounded-full font-semibold bg-[#1a1a2e] text-white hover:bg-[#2a2a3e] active:bg-[#1a1a2e] transition-all px-8 py-3 text-[15px] shadow-lg shadow-gray-900/10 whitespace-nowrap"
              >
                <UIcon name="i-lucide-message-circle" class="w-4 h-4 mr-2" />
                {{ $t("navigation.pageErreur.support") }}
              </UButton>
            </div>
          </div>

          <!-- Helpful Links -->
          <div class="err-anim err-fondu" style="animation-delay: 0.6s">
            <div class="mt-12 pt-8 border-t border-gray-200/50">
              <p class="text-sm text-gray-500 mb-4">{{ $t("navigation.pageErreur.liensUtiles") }}</p>
              <div class="flex flex-wrap gap-4 sm:gap-6 justify-center text-sm font-medium">
                <ULink to="/" class="text-gray-600 hover:text-main transition-colors">{{ $t("contact.filAriane.accueil") }}</ULink>
                <ULink to="/gratuit" class="text-gray-600 hover:text-main transition-colors">{{ $t("association.rightColumn.gratuit") }}</ULink>
                <ULink to="/logiciel-association" class="text-gray-600 hover:text-main transition-colors">{{ $t("navigation.pageErreur.fonctionnalites") }}</ULink>
                <ULink to="/help" class="text-gray-600 hover:text-main transition-colors">{{ $t("navigation.appFooter.colonnes.ressources.centreAide") }}</ULink>
                <ULink to="/contact" class="text-gray-600 hover:text-main transition-colors">{{ $t("contact.filAriane.contact") }}</ULink>
              </div>
            </div>
          </div>
        </div>
      </UContainer>
    </div>

    <LazyVisibleAppFooter hydrate-on-visible />
  </div>
</template>

<style scoped>
/* Fondus de la page d'erreur en CSS pur (audit perf 2026-09-14).
   Ils utilisaient `motion-v` — 129 Ko, soit 15 % du chunk d'ENTRÉE. Comme
   `error.vue` fait partie du graphe de démarrage de TOUTES les pages, chaque
   visiteur téléchargeait une bibliothèque d'animation entière pour un écran
   qu'il ne verra probablement jamais. Le rendu est identique.
   `prefers-reduced-motion` est respecté : contenu affiché, sans mouvement. */
.err-anim {
  animation-duration: 0.5s;
  animation-fill-mode: both;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
}
.err-zoom { animation-name: err-zoom-in; animation-duration: 0.6s; }
.err-monte { animation-name: err-monte-in; }
.err-fondu { animation-name: err-fondu-in; }
@keyframes err-zoom-in { from { opacity: 0; transform: scale(0.8); } to { opacity: 1; transform: none; } }
@keyframes err-monte-in { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: none; } }
@keyframes err-fondu-in { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .err-anim { animation: none; opacity: 1; transform: none; }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}
@keyframes float-slow {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-15px); }
}
@keyframes float-slower {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}
.animate-float { animation: float 3s ease-in-out infinite; }
.animate-float-slow { animation: float-slow 4s ease-in-out infinite; }
.animate-float-slower { animation: float-slower 5s ease-in-out infinite; }
</style>
